import App from "./App";
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import {addMinutes} from "date-fns";
import React from "react";
import ReactDOM from "react-dom/client";
import ReactModal from "react-modal";
import {QueryClient, QueryClientProvider} from "react-query";
import {BrowserRouter} from "react-router-dom";

import Core from "liberty-core";

import {AlertProvider} from "lib/alert";
import * as auth from "lib/auth";
import {Provider as AuthProvider} from "lib/auth";
import {getAuthData, setAuthData, AuthData} from "lib/authStorage";
import {AppointmentProvider} from "lib/newAppointment";

const signOutSessionExpired = () =>
  auth
    .signOut()
    .then(() => setAuthData(undefined))
    .then(() => window.location.reload());

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function prepareMockServiceWorker() {
  if (process.env.REACT_APP_MOCK_ENABLED === "true") {
    return import("liberty-core/mocks/browser").then(({worker}) => {
      return worker.start({onUnhandledRequest: "bypass"});
    });
  }

  return Promise.resolve();
}

function initReact() {
  const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement,
  );
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthProvider authenticatedUser={null}>
          <AlertProvider>
            <AppointmentProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </AppointmentProvider>
          </AlertProvider>
        </AuthProvider>
      </QueryClientProvider>
    </React.StrictMode>,
  );
  ReactModal.setAppElement("#root");
}

const TIMESTAMP_STORAGE_KEY = "lastNetworkRequest";
const SESSION_LOGOUT_MINUTES = 15;

Core.init({
  baseURL: process.env.REACT_APP_API_ROOT,
  getAuthData: async (lastNetworkRequest: Date) => {
    try {
      await localStorage.setItem(
        TIMESTAMP_STORAGE_KEY,
        lastNetworkRequest.toISOString(),
      );
    } catch {
      // do nothing
    }

    const fifteenFromLastRequest = addMinutes(
      lastNetworkRequest,
      SESSION_LOGOUT_MINUTES,
    );

    return {
      accessToken: (await getAuthData())?.accessToken ?? null,
      // TODO: remove fifteenFromLastRequest whenever backend implements expire/refresh
      expireDate: fifteenFromLastRequest,
      refreshToken: "refresh-token",
    };
  },
  setAuthData: (newAuthData: AuthData | undefined) => {
    setAuthData(newAuthData);
    return Promise.resolve();
  },
  onSignOut: signOutSessionExpired,
}).then(() => {
  prepareMockServiceWorker().then(initReact);
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
